import styled from 'styled-components';
import {media} from '../../styles/components/media-queries';

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 80rem;
  margin: 4rem auto 4rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const PageTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 3.2rem;
  font-weight: lighter;
  margin-bottom: 3rem;
  ${media.smaller`
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
  `};
`;

// ─── Step Indicator ──────────────────────────────────────────────────────────

export const StepIndicatorWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
`;

export const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
`;

export const StepDot = styled.div`
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  border: 0.2rem solid
    ${p =>
      p.$active
        ? p.theme.secondaryColor
        : p.$done
        ? p.theme.secondaryColor
        : p.theme.borderColor};
  background-color: ${p =>
    p.$active
      ? p.theme.secondaryColor
      : p.$done
      ? 'transparent'
      : 'transparent'};
  color: ${p =>
    p.$active
      ? p.theme.primaryTextColor || '#fff'
      : p.$done
      ? p.theme.secondaryColor
      : p.theme.tertiaryTextColor || '#999'};
`;

export const StepLabel = styled.span`
  font-size: 1.2rem;
  text-align: center;
  color: ${p =>
    p.$active ? p.theme.secondaryColor : p.theme.tertiaryTextColor || '#999'};
  font-weight: ${p => (p.$active ? 'bold' : 'normal')};
  white-space: nowrap;
  ${media.smaller`
    display: none;
  `};
`;

export const StepLine = styled.div`
  flex: 1;
  height: 0.2rem;
  margin: 0 0.8rem;
  margin-bottom: 2.4rem;
  background-color: ${p =>
    p.$done ? p.theme.secondaryColor : p.theme.borderColor};
  transition: background-color 0.2s;
  ${media.smaller`
    margin-bottom: 0;
  `};
`;

// ─── Location Banner ──────────────────────────────────────────────────────────

export const LocationBanner = styled.div`
  padding: 2.4rem;
  background-color: rgba(211, 47, 47, 0.06);
  border: 0.1rem solid #d32f2f;
  border-radius: 0.4rem;
  text-align: center;
  margin-bottom: 2rem;

  p {
    font-size: 1.6rem;
    color: #d32f2f;
    margin: 0 0 1.6rem;
  }
`;

// ─── Cards ────────────────────────────────────────────────────────────────────

export const StepCard = styled.div`
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  padding: 2.4rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
  position: relative;
`;

export const SectionTitle = styled.h2`
  color: ${p => p.theme.secondaryColor};
  font-size: 2rem;
  font-weight: lighter;
  margin: 0 0 2rem;
`;

// ─── Drop Zone ────────────────────────────────────────────────────────────────

export const DropZone = styled.div`
  border: 0.2rem dashed
    ${p => (p.$isDragOver ? p.theme.secondaryColor : p.theme.borderColor)};
  border-radius: 0.4rem;
  padding: 3.2rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: ${p =>
    p.$isDragOver ? 'rgba(0,128,0,0.04)' : 'transparent'};

  &:hover {
    border-color: ${p => p.theme.secondaryColor};
  }

  p {
    font-size: 1.4rem;
    color: ${p => p.theme.tertiaryTextColor || '#777'};
    margin: 0.4rem 0 0;
  }
`;

export const DropZoneLabel = styled.p`
  font-size: 1.8rem !important;
  font-weight: bold;
  color: ${p => p.theme.secondaryColor} !important;
`;

export const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 1rem;
  margin-top: 1.6rem;
`;

export const PhotoCard = styled.div`
  position: relative;
  border-radius: 0.4rem;
  overflow: hidden;
  aspect-ratio: 1;
  border: 0.1rem solid ${p => p.theme.borderColor};

  button {
    position: absolute;
    top: 0.3rem;
    right: 0.3rem;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    border: none;
    cursor: pointer;
    font-size: 1.4rem;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
`;

export const PhotoThumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1.6rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const FileRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1.2rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#f5f5f5'};
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: ${p => p.theme.textColor};
  gap: 1rem;
`;

export const FileRowName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.8rem;
  line-height: 1;
  color: ${p => p.theme.tertiaryColor || '#d32f2f'};
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

// ─── Form Fields ──────────────────────────────────────────────────────────────

export const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

export const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
  margin-bottom: 0.6rem;

  span {
    font-weight: normal;
    color: ${p => p.theme.tertiaryTextColor || '#999'};
    margin-left: 0.4rem;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  font-size: 1.6rem;
  border: 0.1rem solid ${p => (p.$hasError ? '#d32f2f' : p.theme.borderColor)};
  border-radius: 0.4rem;
  color: ${p => p.theme.textColor};
  background-color: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${p => (p.$hasError ? '#d32f2f' : p.theme.secondaryColor)};
  }
`;

export const FieldError = styled.p`
  font-size: 1.3rem;
  color: #d32f2f;
  margin: 0.4rem 0 0;
`;

// ─── Resolved Data Fields ─────────────────────────────────────────────────────

export const ResolvedFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.6rem 2.4rem;
  margin-top: 1.6rem;
  ${media.smaller`
    grid-template-columns: 1fr;
  `};
`;

export const ResolvedFieldCard = styled.div`
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  padding: 1.2rem 1.4rem;
  background-color: ${p => p.theme.lightBackgroundColor || '#fafafa'};
`;

export const FieldLabel = styled.div`
  font-size: 1.2rem;
  text-transform: uppercase;
  color: ${p => p.theme.tertiaryTextColor || '#888'};
  margin-bottom: 0.4rem;
  letter-spacing: 0.05em;
`;

export const FieldValueRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

export const FieldValue = styled.span`
  font-size: 1.5rem;
  color: ${p => p.theme.textColor};
  flex: 1;
  word-break: break-word;
`;

export const FieldNullValue = styled.span`
  font-size: 1.4rem;
  color: ${p => p.theme.tertiaryTextColor || '#bbb'};
  font-style: italic;
`;

export const FieldSource = styled.span`
  font-size: 1.1rem;
  color: ${p => p.theme.tertiaryTextColor || '#aaa'};
  white-space: nowrap;
`;

export const EditButton = styled.button`
  background: none;
  border: 0.1rem solid ${p => p.theme.secondaryColor};
  border-radius: 0.3rem;
  color: ${p => p.theme.secondaryColor};
  font-size: 1.2rem;
  padding: 0.2rem 0.7rem;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background-color: ${p => p.theme.secondaryColor};
    color: ${p => p.theme.primaryTextColor || '#fff'};
  }
`;

export const InlineEditRow = styled.div`
  display: flex;
  gap: 0.6rem;
  margin-top: 0.4rem;

  input {
    flex: 1;
    padding: 0.5rem 0.8rem;
    font-size: 1.4rem;
    border: 0.1rem solid ${p => p.theme.secondaryColor};
    border-radius: 0.3rem;
    outline: none;
  }
`;

export const SaveButton = styled.button`
  padding: 0.5rem 1rem;
  font-size: 1.3rem;
  background-color: ${p => p.theme.secondaryColor};
  color: ${p => p.theme.primaryTextColor || '#fff'};
  border: none;
  border-radius: 0.3rem;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CancelEditButton = styled.button`
  padding: 0.5rem 0.8rem;
  font-size: 1.3rem;
  background: none;
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.3rem;
  cursor: pointer;
  color: ${p => p.theme.textColor};
`;

// ─── Processing Step ──────────────────────────────────────────────────────────

export const ProcessingWrapper = styled.div`
  text-align: center;
  padding: 3.2rem 2rem;
`;

export const ProcessingTitle = styled.h3`
  font-size: 2rem;
  color: ${p => p.theme.secondaryColor};
  font-weight: lighter;
  margin: 0 0 1rem;
`;

export const ProcessingStatusText = styled.p`
  font-size: 1.5rem;
  color: ${p => p.theme.tertiaryTextColor || '#777'};
  margin: 0;
`;

export const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
`;

// ─── Status Badge ─────────────────────────────────────────────────────────────

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.9rem;
  border-radius: 1.2rem;
  font-size: 1.2rem;
  font-weight: bold;
  background-color: ${p => {
    switch (p.$status) {
      case 'VERIFIED':
        return '#1976d2';
      case 'ACCOUNT_CREATED':
        return '#2e7d32';
      case 'FAILED':
        return '#d32f2f';
      default:
        return '#757575';
    }
  }};
  color: #fff;
`;

// ─── Messages ─────────────────────────────────────────────────────────────────

export const ErrorMessage = styled.div`
  margin-top: 1.2rem;
  padding: 1.2rem 1.4rem;
  background-color: rgba(211, 47, 47, 0.07);
  border: 0.1rem solid #d32f2f;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #d32f2f;
`;

export const SuccessCard = styled.div`
  text-align: center;
  padding: 3.2rem 2rem;
  border: 0.1rem solid #2e7d32;
  border-radius: 0.4rem;
  background-color: rgba(46, 125, 50, 0.05);

  h2 {
    font-size: 2.4rem;
    color: #2e7d32;
    font-weight: lighter;
    margin: 0 0 1rem;
  }

  p {
    font-size: 1.5rem;
    color: ${p => p.theme.textColor};
    margin: 0 0 0.4rem;
  }
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.2rem;
  margin-top: 2.4rem;
  flex-wrap: wrap;
`;

export const PrimaryButton = styled.button`
  padding: 1.1rem 2.8rem;
  font-size: 1.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: ${p => p.theme.primaryTextColor || '#fff'};
  background-color: ${p => p.theme.secondaryColor};
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const SecondaryButton = styled.button`
  padding: 1.1rem 2.4rem;
  font-size: 1.5rem;
  color: ${p => p.theme.secondaryColor};
  background: none;
  border: 0.1rem solid ${p => p.theme.secondaryColor};
  border-radius: 0.4rem;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.75;
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
  z-index: 2;
  flex-direction: column;
  gap: 1.2rem;

  p {
    font-size: 1.5rem;
    color: ${p => p.theme.secondaryColor};
  }
`;

export const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4rem 2rem;
  gap: 1.6rem;
  text-align: center;

  p {
    font-size: 1.5rem;
    color: ${p => p.theme.textColor};
    margin: 0;
  }
`;

// ─── Camera ───────────────────────────────────────────────────────────────────

export const CameraOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

export const VideoPreview = styled.video`
  width: 100%;
  max-width: 64rem;
  max-height: 72vh;
  object-fit: cover;
  display: block;
  background: #111;
`;

export const CameraControls = styled.div`
  display: flex;
  align-items: center;
  gap: 2.4rem;
  margin-top: 2.4rem;
`;

export const CaptureButton = styled.button`
  width: 7.2rem;
  height: 7.2rem;
  border-radius: 50%;
  background: #fff;
  border: 0.4rem solid rgba(255, 255, 255, 0.4);
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.1s, opacity 0.1s;
  box-shadow: 0 0 0 0.3rem #000, 0 0 0 0.6rem rgba(255, 255, 255, 0.5);

  &:active {
    transform: scale(0.92);
    opacity: 0.85;
  }
`;

// ─── Photo Preview Overlay ────────────────────────────────────────────────────

export const PhotoPreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  cursor: pointer;
  padding: 2rem;

  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 0.4rem;
    box-shadow: 0 0.8rem 3.2rem rgba(0, 0, 0, 0.6);
  }
`;

// ─── Section Hint ─────────────────────────────────────────────────────────────

export const SectionHint = styled.p`
  font-size: 1.3rem;
  color: ${p => p.theme.tertiaryTextColor || '#888'};
  margin: -1.2rem 0 1.6rem;
`;

